/**
 * `useVehicleTelemetry` - the canonical field-level telemetry store of the
 * tracking page (spec `vehicle-realtime-data-consistency`, design "Hook
 * Interface", implementation steps 5-7).
 *
 * Called **once** in `VehicleContainer`, it reconciles the three sources that
 * report vehicle movement:
 *
 *   1. `GET /formatted/latest/vehicles` (`trackingData.latest_data`) - a per-field
 *      aggregate, hydrated here (design step 6),
 *   2. `GET /formatted/vehicles/{plate}` - route packets, hydrated by consumers
 *      through `hydrateRoute` (design step 7),
 *   3. SignalR - live packets, accumulated per plate by `useWebSocket`.
 *
 * Reconciliation is timestamp-based, never response-order based: each field is
 * replaced only by a newer observation *of that field*, so the sidebar, the live
 * summary and the status icon all read the same canonical `movement` object
 * (#119201) and generic packet activity can never refresh movement freshness
 * (#119052).
 *
 * Usage:
 * ```js
 * const {
 *   getCurrentSpeed,
 *   getCurrentMovementByPlateNumber,
 *   getCurrentTelemetryByPlateNumber,
 *   getTableDataByPlateNumber,
 *   hydrateRoute,
 * } = useVehicleTelemetry({
 *   latestVehicleData: trackingData?.latest_data,
 *   realtimeByPlate,
 *   selectedPlateNumber: selectedVehiclePlate,
 * });
 * ```
 *
 * The returned getters are the pure selectors of `./selectors` bound to the
 * hook's state, so the same reconciliation is testable without rendering.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import { initialState, telemetryReducer } from "./reducer";
import {
  getCurrentMovementByPlateNumber as selectCurrentMovement,
  getCurrentMovementObservedAt as selectCurrentMovementObservedAt,
  getCurrentSpeed as selectCurrentSpeed,
  getCurrentTelemetryByPlateNumber as selectCurrentTelemetry,
  getNewestSpeedRowByPlateNumber as selectNewestSpeedRow,
  getTableDataByPlateNumber as selectTableData,
} from "./selectors";

const isValidPlate = (plateNumber) =>
  plateNumber !== null && plateNumber !== undefined && plateNumber !== "";

/**
 * Normalizes the realtime cache of `useWebSocket` into a packet list. The cache
 * is keyed by plate number (`upsertPacketByPlate`), but an array is accepted too
 * so a caller can pass raw packets directly.
 */
const toPacketList = (realtimeByPlate) => {
  if (!realtimeByPlate) return [];
  if (Array.isArray(realtimeByPlate)) return realtimeByPlate.filter(Boolean);
  if (typeof realtimeByPlate !== "object") return [];
  return Object.values(realtimeByPlate).filter(Boolean);
};

export function useVehicleTelemetry({
  latestVehicleData,
  realtimeByPlate,
  selectedPlateNumber,
} = {}) {
  const [state, dispatch] = useReducer(telemetryReducer, initialState);

  // Design step 6: hydrate the current field state from the latest-vehicles
  // aggregate. Each field is merged on its own observation time, so a refreshed
  // generic source_time cannot make an old speed look current.
  useEffect(() => {
    const items = Array.isArray(latestVehicleData)
      ? latestVehicleData
      : [latestVehicleData].filter(Boolean);
    if (!items.length) return;

    dispatch({ type: "HYDRATE_LATEST_VEHICLES", payload: items });
  }, [latestVehicleData]);

  // Design step 7 (live half): merge the accumulated SignalR packets. The cache
  // retains every vehicle, so a packet for another plate cannot evict the
  // selected vehicle's data.
  useEffect(() => {
    const packets = toPacketList(realtimeByPlate);
    if (!packets.length) return;

    dispatch({ type: "MERGE_REALTIME", payload: packets });
  }, [realtimeByPlate]);

  // Selection change isolates the live table: the previous vehicle's packets are
  // dropped so they can never be rendered for the newly selected plate. Current
  // field state is kept - the sidebar still needs every vehicle's movement.
  const previousPlateRef = useRef(selectedPlateNumber);
  useEffect(() => {
    const previousPlate = previousPlateRef.current;
    previousPlateRef.current = selectedPlateNumber;

    if (previousPlate === selectedPlateNumber || !isValidPlate(previousPlate)) return;

    dispatch({ type: "CLEAR_TABLE", plateNumber: previousPlate });
  }, [selectedPlateNumber]);

  /**
   * Design step 7 (route half): merges route packets of one plate into both the
   * live table and the current field state.
   *
   * @param {string} plateNumber - Plate the packets belong to.
   * @param {Array<Object>|Object} packets - Raw route packets.
   */
  const hydrateRoute = useCallback((plateNumber, packets) => {
    if (!isValidPlate(plateNumber) || !packets) return;

    const payload = Array.isArray(packets) ? packets.filter(Boolean) : [packets];
    if (!payload.length) return;

    dispatch({ type: "HYDRATE_ROUTE", plateNumber, payload });
  }, []);

  /** Clears table rows for a plate (or every plate when omitted). */
  const clearTable = useCallback((plateNumber) => {
    dispatch({ type: "CLEAR_TABLE", plateNumber });
  }, []);

  const getCurrentSpeed = useCallback(
    (plateNumber) => selectCurrentSpeed(state, plateNumber),
    [state]
  );
  const getCurrentMovementByPlateNumber = useCallback(
    (plateNumber) => selectCurrentMovement(state, plateNumber),
    [state]
  );
  const getCurrentMovementObservedAt = useCallback(
    (plateNumber) => selectCurrentMovementObservedAt(state, plateNumber),
    [state]
  );
  const getCurrentTelemetryByPlateNumber = useCallback(
    (plateNumber) => selectCurrentTelemetry(state, plateNumber),
    [state]
  );
  const getTableDataByPlateNumber = useCallback(
    (plateNumber) => selectTableData(state, plateNumber),
    [state]
  );
  const getNewestSpeedRowByPlateNumber = useCallback(
    (plateNumber) => selectNewestSpeedRow(state, plateNumber),
    [state]
  );

  return useMemo(
    () => ({
      telemetryState: state,
      getCurrentSpeed,
      getCurrentMovementByPlateNumber,
      getCurrentMovementObservedAt,
      getCurrentTelemetryByPlateNumber,
      getTableDataByPlateNumber,
      getNewestSpeedRowByPlateNumber,
      hydrateRoute,
      clearTable,
    }),
    [
      state,
      getCurrentSpeed,
      getCurrentMovementByPlateNumber,
      getCurrentMovementObservedAt,
      getCurrentTelemetryByPlateNumber,
      getTableDataByPlateNumber,
      getNewestSpeedRowByPlateNumber,
      hydrateRoute,
      clearTable,
    ]
  );
}

export default useVehicleTelemetry;
